import { logger } from '../logging';

export type ResolveRemoteUrlFunction = (remoteName: string) => string | Promise<string>;

declare const __webpack_init_sharing__: (scope: 'default') => Promise<void>;
declare const __webpack_share_scopes__: { default: unknown };

let resolveRemoteUrl: ResolveRemoteUrlFunction;

export function setRemoteUrlResolver(_resolveRemoteUrl: ResolveRemoteUrlFunction) {
  resolveRemoteUrl = _resolveRemoteUrl;
}

let remoteUrlDefinitions: Record<string, string>;

export function setRemoteDefinitions(definitions: Record<string, string>) {
  remoteUrlDefinitions = definitions;
}

const remoteModuleMap = new Map<string, unknown>();
const remoteContainerMap = new Map<string, unknown>();

export async function loadRemoteModule(remoteName: string, moduleName: string) {
  const remoteModuleKey = `${remoteName}:${moduleName}`;
  if (remoteModuleMap.has(remoteModuleKey)) {
    return remoteModuleMap.get(remoteModuleKey);
  }
  const container = remoteContainerMap.has(remoteName)
    ? remoteContainerMap.get(remoteName)
    : await loadRemoteContainer(remoteName);

  const factory = await container.get(moduleName);
  const Module = factory();

  remoteModuleMap.set(remoteModuleKey, Module);
  logger.info(`Successfully mounted ${moduleName} component from ${remoteName}.`);
  return Module;
}

function loadModule(url: string) {
  return import(/* webpackIgnore:true */ url);
}

/**
 * A single entry in webpack's share scope, plus the extra fields the Module
 * Federation v2 runtime expects to find on one.
 */
/**
 * Remotes built with Module Federation v2 (Vite/Rspack) rather than webpack's
 * built-in plugin. Everything else takes the untouched code path below.
 */
const MF_V2_REMOTES = new Set([
  'extracts',
  'product-hub-ui',
  'foundry',
  'try-foundry',
  'brand-insights-ui',
  'bi-powertabs',
  'traderoi-ui',
]);

interface ShareScopeEntry {
  /** Set by webpack. */
  get?: () => unknown;
  eager?: boolean;
  loaded?: number | boolean;
  /** Read by the Module Federation v2 runtime; webpack does not set these. */
  version?: string;
  from?: string;
  scope?: string[];
  deps?: string[];
  useIn?: string[];
  shareConfig?: {
    singleton?: boolean;
    requiredVersion?: string | false;
    eager?: boolean;
    strictVersion?: boolean;
  };
}

/**
 * Make webpack's share scope legible to Module Federation v2 remotes.
 *
 * webpack 5's built-in ModuleFederationPlugin (what `@nx/react`'s
 * `withModuleFederation` uses) keeps a shared module's version in the scope
 * KEY — `scope['react']['18.3.1'] = { get, from, eager, loaded }` — and puts no
 * `version` on the entry itself. The MF v2 runtime that bundlers like
 * `@module-federation/vite` ship reads it from the ENTRY instead
 * (`version: entry.version ?? '0'`).
 *
 * So without this, every module we provide looks like version "0" to such a
 * remote, fails its `requiredVersion` range, and is silently rejected — the
 * remote then falls back to its own bundled copy. With React that means two
 * React instances on the page and a null hook dispatcher
 * ("Cannot read properties of null (reading 'useEffect')") the moment the
 * remote renders.
 *
 * Copying the key onto the entry (and filling in the bookkeeping fields the v2
 * runtime expects) makes the negotiation succeed. Existing webpack-built
 * remotes are unaffected: webpack's own consume logic reads the key, never
 * `entry.version`, and ignores the extra fields.
 */
export function annotateShareScopeForV2Remote(
  scope: Record<string, Record<string, ShareScopeEntry>>
) {
  for (const versions of Object.values(scope)) {
    for (const [version, entry] of Object.entries(versions)) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      entry.version ??= version;
      entry.from ??= 'spins-container';
      entry.scope ??= ['default'];
      entry.deps ??= [];
      entry.useIn ??= [];
      // The host shares these eagerly as singletons (module-federation.config.js).
      entry.shareConfig ??= { singleton: true, requiredVersion: false, eager: true };
    }
  }
}

let initialSharingScopeCreated = false;

async function loadRemoteContainer(remoteName: string) {
  if (!resolveRemoteUrl && !remoteUrlDefinitions) {
    throw new Error(
      'Call setRemoteDefinitions or setRemoteUrlResolver to allow Dynamic Federation to find the remote apps correctly.'
    );
  }

  if (!initialSharingScopeCreated) {
    initialSharingScopeCreated = true;
    await __webpack_init_sharing__('default');
  }

  const remoteUrl = remoteUrlDefinitions ? remoteUrlDefinitions[remoteName] : await resolveRemoteUrl(remoteName);

  if (!remoteUrl) {
    throw new Error(`Remote not found, name: ${remoteName}, url: ${remoteUrl}`);
  }

  const containerUrl = `${remoteUrl}${remoteUrl.endsWith('/') ? '' : '/'}remoteEntry.js`;

  const container = await loadModule(containerUrl);
  if (MF_V2_REMOTES.has(remoteName)) {
    annotateShareScopeForV2Remote(
      __webpack_share_scopes__.default as Record<string, Record<string, ShareScopeEntry>>
    );
  }
  await container.init(__webpack_share_scopes__.default);

  remoteContainerMap.set(remoteName, container);
  return container;
}
